import {
  capitalizeFirstLetter,
  ensureFirstLetterLowercase,
  paginationDataStr,
  paginationDataStrWithoutTotal,
} from "@/lib/utils";
import { buildNestedDataFieldsForRelations } from "@repo/utils/utils";
import { gql } from "graphql-request";
import { FormField } from "@/hooks/schema/use-schema";

type QueryListDocumentType = {
  type: "QueryList";
};

type QueryByIdDocumentType = {
  type: "QueryById";
  id: string;
};

type MutationCreateDocumentType = {
  type: "MutationCreate";
};

type MutationUpdateDocumentType = {
  type: "MutationUpdate";
};

type MutationDeleteDocumentType = {
  type: "MutationDelete";
};

export const getDocumentPartForQuery = async ({
  entitySchema,
  documentType,
  fetchRelations = false,
  fieldsToFetch,
  fieldsToFetchForRelationFieldNames,
  cursor,
}: {
  entitySchema: { fields: Array<FormField>; name: string };
  documentType: QueryListDocumentType | QueryByIdDocumentType;
  fetchRelations?: boolean;
  fieldsToFetch?: Array<string>;
  fieldsToFetchForRelationFieldNames?: Array<{
    fieldName: string;
    fieldsToFetch: Array<string>;
  }>;
  cursor?: number;
}) => {
  if (!entitySchema) {
    throw Error("no schema provided");
  }

  if (!entitySchema.fields || entitySchema.fields.length < 0) {
    throw Error("not enought fields");
  }

  let documentString = "";

  const { queryFieldsString, paginationVariables } = getQueryFieldsStringWithVariables({
    entitySchema,
    fetchRelations,
    fieldsToFetch,
    fieldsToFetchForRelationFieldNames,
  });

  const variableString = getPaginationVariablesString(paginationVariables);

  if (documentType.type === "QueryById") {
    documentString = gql`
    query ${ensureFirstLetterLowercase(entitySchema.name)} ($id: ID! ${variableString}) {
      ${ensureFirstLetterLowercase(entitySchema.name)}ById(id: $id) {
        data {
          ${queryFieldsString}
        }
      }
    }`;
  } else if (documentType.type === "QueryList") {
    documentString = gql`
    query ${ensureFirstLetterLowercase(entitySchema.name)} ($filter: FilterInput ${variableString}) {
      ${ensureFirstLetterLowercase(entitySchema.name)}List (
        pagination: {
          page: ${cursor},
          per_page: 20
        }
        filter: $filter
      ) {
        data {
          ${queryFieldsString}
        }
        ${paginationDataStr}
      }
    }`;
  }

  return { documentString, variableString };
};

export const getDocumentPartForMutation = async ({
  entitySchema,
  documentType,
  fetchRelations = false,
}: {
  entitySchema: { fields: Array<FormField>; name: string };
  documentType:
    | MutationCreateDocumentType
    | MutationUpdateDocumentType
    | MutationDeleteDocumentType;
  fetchRelations?: boolean;
}) => {
  if (!entitySchema) {
    throw Error("no schema provided");
  }

  if (!entitySchema.fields || entitySchema.fields.length < 0) {
    throw Error("not enought fields");
  }

  const queryFieldsString = getQueryFieldsString(entitySchema, fetchRelations);

  let documentString = "";

  if (documentType.type === "MutationCreate") {
    documentString = gql`
    mutation ${entitySchema.name} ($input: Create${capitalizeFirstLetter(entitySchema.name)}Input!) {
      create${entitySchema.name}(input: $input) {
        data {
          ${queryFieldsString}
        }
      }
    }
    `;
  } else if (documentType.type === "MutationUpdate") {
    documentString = gql`
    mutation ${entitySchema.name} ($input: Update${capitalizeFirstLetter(entitySchema.name)}Input!) {
      update${entitySchema.name}(input: $input) {
        data {
          ${queryFieldsString}
        }
      }
    }
    `;
  } else if (documentType.type === "MutationDelete") {
    documentString = gql`
    mutation ${entitySchema.name} ($ids: [ID!]!) {
      delete${entitySchema.name}(ids: $ids) {
        data {
          success
        }        
      }
    }
    `;
  }
  return documentString;
};

const getQueryFieldsString = (
  entitySchema: { fields: Array<FormField>; name: string },
  fetchRelations: boolean,
) => {
  return entitySchema.fields
    .filter((field) => field.kind === "SCALAR" || (fetchRelations && field.kind === "OBJECT"))
    .map((field) => {
      if (field.kind === "OBJECT") {
        const dataFields = field?.fields?.filter((field) => field.kind === "SCALAR");

        return ` ${field.name} {
                data {
                    ${dataFields?.map((field) => field.name).join("\n")}
                    }
            }
            `;
      }
      return field.name;
    })
    .join("\n");
};

/**
 * The subscription projection MUST be built by the same builder as the list
 * query, so event payloads always have the exact row shape the table renders —
 * including `<field>_variants { data { is_default value variant } }` for
 * variant fields and `relation { data { id … } }` for relations. The one
 * difference: a stored subscription selection is replayed later by the
 * websocket-service in its own query, where the list's `$…Pagination`
 * variables don't exist — so to-many pagination args are inlined as literals
 * (same page/per_page the list uses for cell display).
 */
export const getSubscriptionFieldsSelection = ({
  entitySchema,
  fieldsToFetch,
  fieldsToFetchForRelationFieldNames,
}: {
  entitySchema: { fields: Array<FormField>; name: string };
  fieldsToFetch?: Array<string>;
  fieldsToFetchForRelationFieldNames?: Array<{
    fieldName: string;
    fieldsToFetch: Array<string>;
  }>;
}): string => {
  const { queryFieldsString } = getQueryFieldsStringWithVariables({
    entitySchema,
    fetchRelations: false,
    fieldsToFetch,
    fieldsToFetchForRelationFieldNames,
    inlinePagination: { page: 1, per_page: 3 },
  });
  return queryFieldsString;
};

const getQueryFieldsStringWithVariables = ({
  entitySchema,
  fetchRelations,
  fieldsToFetch,
  fieldsToFetchForRelationFieldNames,
  inlinePagination,
}: {
  entitySchema: { fields: Array<FormField>; name: string };
  fetchRelations: boolean;
  fieldsToFetch?: Array<string>;
  fieldsToFetchForRelationFieldNames?: Array<{
    fieldName: string;
    fieldsToFetch: Array<string>;
  }>;
  /**
   * Emit to-many pagination args as literals instead of `$…Pagination`
   * variables — required when the selection is stored and replayed outside
   * this document (subscription projections).
   */
  inlinePagination?: { page: number; per_page: number };
}) => {
  const paginationVariables = new Array<string>();
  const relationFieldsToFetchForFieldName = new Map<string, string[]>();

  const scalarFieldsToFetch = fieldsToFetch?.filter((field) => !field.includes(".")) ?? [];

  const fieldsToFetchThatHaveRelations =
    fieldsToFetch
      ?.filter((field) => {
        return field.includes(".");
      })
      .map((field) => {
        const [relationField, ...rest] = field.split(".");
        // Keep the full sub-path so deeper to-one chains ("address.city") survive; a
        // single-hop path collapses to just the leaf ("name").
        const relationFieldToFetch = rest.join(".");

        const existingRelationFields = relationFieldsToFetchForFieldName.get(relationField) ?? [];
        relationFieldsToFetchForFieldName.set(relationField, [
          ...existingRelationFields,
          relationFieldToFetch,
        ]);

        return relationField;
      }) ?? [];

  const firstLevelFieldsThatWeWantToFetch = [
    ...scalarFieldsToFetch,
    ...fieldsToFetchThatHaveRelations,
  ];

  const queryFieldsString = entitySchema.fields
    .filter((field) => {
      if (firstLevelFieldsThatWeWantToFetch) {
        return (
          field.name === "id" || // Always include id field
          firstLevelFieldsThatWeWantToFetch?.includes(field.name) ||
          firstLevelFieldsThatWeWantToFetch?.includes(field.name.replace("_variants", ""))
        );
      }
      return true;
    })
    .filter((field) => {
      if (field.kind === "SCALAR") {
        return true;
      }
      // Check if this field is specifically requested in fieldsToFetchForRelationFieldNames
      // const isRequestedRelationField = fieldsToFetchForRelationFieldNames?.some(
      //   (relationField) => relationField.fieldName === field.name,
      // );

      const isRequestedRelationField = relationFieldsToFetchForFieldName.get(field.name) ?? [];

      return (
        (fetchRelations && field.kind === "OBJECT") ||
        isRequestedRelationField || // Include relation fields that are specifically requested
        field.type === "FieldVariantsData"
      );
    })
    .map((field) => {
      if (field.kind === "OBJECT") {
        // Deep to-one relation chain (e.g. "customer.address.city"): emit a nested
        // selection set. Only to-one relations are ever requested at depth (the timeline
        // label picker blocks to-many), so no inner pagination is needed. `id` is always
        // included so each relation record stays identifiable.
        const subPaths = relationFieldsToFetchForFieldName.get(field.name) ?? [];
        if (subPaths.some((p) => p.includes("."))) {
          const dataInner = buildNestedDataFieldsForRelations(["id", ...subPaths]);
          return ` ${field.name} {
                data {
                    ${dataInner}
                    }
            }
            `;
        }

        // FIXME: i left of here; we have to figure out the fetching of the relation fields in the
        // connect and disconnect relations component; otherwise i won't have data there

        const fieldsObject = fieldsToFetchForRelationFieldNames?.find(
          (x) => x.fieldName === field.name,
        );

        const fieldsToFetch = fieldsObject?.fieldsToFetch ?? ["id"];

        const dataFields =
          field?.fields
            ?.filter((field) => field.kind === "SCALAR")
            ?.filter((subField) => {
              // For variant fields (FieldVariantsData), always include the standard fields
              if (
                field.type === "FieldVariantsData" &&
                (subField.name === "is_default" ||
                  subField.name === "value" ||
                  subField.name === "variant")
              ) {
                return true;
              }
              return fieldsToFetch?.includes(subField.name);
            }) ?? [];

        // Ensure we always have at least one field to fetch
        const fieldsToInclude =
          dataFields.length > 0
            ? dataFields
            : (field?.fields?.filter((f) => f.kind === "SCALAR" && f.name === "id") ?? []);

        let paginationStr = "";
        //todo: fixme! this will lead to an error if the entityName ends with 'List'
        if (field.type?.endsWith("ListData")) {
          if (inlinePagination) {
            paginationStr = `(pagination: {page: ${inlinePagination.page}, per_page: ${inlinePagination.per_page}})`;
          } else {
            const variable = `$${field.name}Pagination`;
            paginationVariables.push(variable);
            paginationStr = `(pagination: ${variable})`;
          }
        }

        const stringValue = ` ${field.name} ${paginationStr} {
                data {
                    ${fieldsToInclude?.map((field) => field.name).join("\n")}
                    ${
                      //TODO: THIS IS A HOTFIX ONLY! THIS SHOULD BE REMOVED AND WE SHOULD FIX THE PARSING OF INTROSPECTION QUERY
                      field.type === "FieldVariantsData" && fieldsToInclude?.length === 0
                        ? `
                      is_default
                      value
                      variant
                    `
                        : ""
                    }
                    }
                ${
                  // TODO: Re-enable paginationDataStr (with total) when backend bug is fixed
                  !!paginationStr ? paginationDataStrWithoutTotal : ""
                }
            }
            `;

        return stringValue;
      }

      return field.name;
    })
    .join("\n");

  // const queryStringNew = fieldsToFetch
  //   ?.map((field) => {
  //     const fieldInSchema = entitySchema.fields.find((f) => f.name === field);

  //     if (field.includes('.')) {
  //       const relationField = field.split('.')[0];
  //       const relationFieldToFetch = field.split('.')[1];

  //       return ` ${relationField} {
  //       data {
  //         ${relationFieldToFetch}
  //       }

  //   }`;
  //     }
  //     return ` ${field}`;
  //   })
  //   .join('\n');

  return { queryFieldsString, paginationVariables };
};

const getPaginationVariablesString = (variables: Array<string>) => {
  let str = "";
  if (variables.length > 0) {
    str = ",";
  }
  str = str + variables.map((x) => `${x}: PaginationInput`).join(",");
  return str;
};
